import {StoreItemCategory, StoreItemType, StoreMapType, StorePresalePackType} from '../entity_datas/StoreEnums'
import {BigId, LobbyImage, StoreExpansionType, ColonistMembershipType} from '@colonist/types'
import {BaseStoreItem, GIFT_MEMBERSHIP_EXPIRY_YEARS} from './BaseStoreItem'
import {BaseMapData} from '../map_data/BaseMapData'
import {BaseGameModeData} from '@colonist/shared-types'
import {ArrayUtils} from '../utils/ArrayUtils'
import {ColonistMembershipProduct} from './ColonistMembershipProduct'
import {TranslationObject} from '@colonist/i18n'
import {EnumUtils, TimeUtils} from '@colonist/utils'
import {PaymentMethod} from './BaseColonistStoreProduct'
import {PlayerColor} from '@colonist/game-states'
import loopEnums = EnumUtils.loopEnums
import {ColonistMembershipPerkType} from '@colonist/lobby-types'
import {StoreItemController} from './StoreItemController'

export interface GiftedMembershipItem {
    id: BigId
    membershipType: ColonistMembershipType
    periods: number
    giftedAt: Date
}

export class UserItems {
    colonistCoins: number
    presalePacks: BaseStoreItem[]
    expansions: BaseStoreItem[]
    maps: BaseStoreItem[]
    icons: BaseStoreItem[]
    colors: BaseStoreItem[]
    membership: ColonistMembershipType | undefined
    membershipPending: boolean
    membershipEndDate: Date | undefined
    membershipPaymentMethod: PaymentMethod | undefined
    giftedMemberships: GiftedMembershipItem[]

    constructor() {
        this.colonistCoins = 0
        this.presalePacks = []
        this.expansions = []
        this.maps = []
        this.icons = []
        this.colors = []
        this.giftedMemberships = []
        this.membership = undefined
        this.membershipPending = false
        this.membershipEndDate = undefined
        this.membershipPaymentMethod = undefined
    }

    static deserialize(data: UserItems): UserItems {
        const items = new UserItems()
        items.colonistCoins = data.colonistCoins
        items.presalePacks = data.presalePacks
        items.expansions = data.expansions
        items.maps = data.maps
        items.icons = data.icons
        items.colors = data.colors
        items.giftedMemberships = data.giftedMemberships
        items.membership = data.membership
        items.membershipPending = data.membershipPending
        items.membershipEndDate = data.membershipEndDate
        items.membershipPaymentMethod = data.membershipPaymentMethod
        return items
    }

    private hasAccessToStoreMap(mapType: StoreMapType[]): boolean {
        if(mapType.length == 0) return true
        for(const map of this.maps) {
            if(ArrayUtils.elementInArray(mapType, map.type)) return true
        }
        return false
    }

    hasAccessToExpansion(expansionType: StoreExpansionType[]): boolean {
        if(expansionType.length == 0) return true
        for(const expansion of this.expansions) {
            if(ArrayUtils.elementInArray(expansionType, expansion.type)) return true
        }
        return false
    }

    hasAccessToPremiumMap(mapData: BaseMapData): boolean {
        if(this.isPerkAvailable(ColonistMembershipPerkType.AllMaps)) return true
        if(!mapData.isMapPremium()) return true

        const isMapSelectionRestrictedByPremiumMode = mapData.isMapRestrictedByPremiumGameMode() && !this.hasAccessToExpansion(mapData.premiumExpansionAccess)
        if(isMapSelectionRestrictedByPremiumMode) return false

        const isMapSelectionRestrictedByPremiumMap = mapData.isMapRestrictedByPremiumMap() && !this.hasAccessToStoreMap(mapData.premiumMapAccess)
        if(isMapSelectionRestrictedByPremiumMap) return false

        return true
    }

    hasAccessToPremiumMode(gameModeData: BaseGameModeData): boolean {
        if(this.isPerkAvailable(ColonistMembershipPerkType.AllExpansions)) return true
        if(!gameModeData.isGameModePremium()) return true

        return this.hasAccessToExpansion(gameModeData.premiumExpansionAccess)
    }

    hasAccessToColor(color: PlayerColor): boolean {
        if(this.isPerkAvailable(ColonistMembershipPerkType.AllColors)) return true
        const freeColors = [PlayerColor.Red, PlayerColor.Blue, PlayerColor.Green, PlayerColor.Orange]
        if(freeColors.includes(color)) return true
        for(const colorItem of this.colors) {
            if(BaseStoreItem.getStorePlayerColorOfPlayerColor(color) == colorItem.type) return true
        }
        return false
    }

    hasAccessToNoTrolls(): boolean {
        if(this.isNoTrollsAvailable) return true
        return false
    }

    hasMembership(): boolean {
        return this.membership != undefined
    }

    // ====================================================================================================================================
    // Presale Packs
    // ====================================================================================================================================

    hasFoundersPack(): boolean {
        const index = this.presalePacks.findIndex((pack: BaseStoreItem) => pack.type == StorePresalePackType.FounderPack && pack.category == StoreItemCategory.PresalePack)
        return index >= 0
    }

    // ====================================================================================================================================
    // Items
    // ====================================================================================================================================

    addGiftedMembership(membership: GiftedMembershipItem) {
        if(TimeUtils.addYears(membership.giftedAt, GIFT_MEMBERSHIP_EXPIRY_YEARS) < new Date()) return
        this.giftedMemberships.push(membership)
    }

    addStoreItem(category: StoreItemCategory, type: StoreItemType) {
        const item = StoreItemController.getItemDetail(category, type)
        if(!item) return
        switch(item.category) {
            case StoreItemCategory.PresalePack: this.presalePacks.push(item); return
            case StoreItemCategory.Expansion: this.expansions.push(item); return
            case StoreItemCategory.Map: this.maps.push(item); return
            case StoreItemCategory.Icon: this.icons.push(item); return
            case StoreItemCategory.Color: this.colors.push(item); return
            case StoreItemCategory.Consumable: return
            case StoreItemCategory.Membership: return
        }
    }

    getAvailableColors(): PlayerColor[] {
        const result: PlayerColor[] = []
        loopEnums(PlayerColor, (_, color) => {
            if(this.hasAccessToColor(color)) {
                result.push(color)
            }
        })

        return result
    }

    // ====================================================================================================================================
    // Membership
    // ====================================================================================================================================

    isPerkAvailable(perk: ColonistMembershipPerkType): boolean {
        if(this.membership == undefined) return false
        const membershipType = ColonistMembershipProduct.getMembershipProduct(this.membership)
        const hasPerk = membershipType.perks.filter((perkItem) => perkItem.type == perk).length > 0
        return hasPerk
    }

    get isNoTrollsAvailable(): boolean {
        if(this.membership == undefined) return false
        return true
    }

    getCurrentMembership(): {type: ColonistMembershipType, image: LobbyImage, pending: boolean, title: TranslationObject} | undefined {
        const currentMembership = this.membership
        if(currentMembership == undefined) return undefined
        const membershipProduct = ColonistMembershipProduct.getMembershipProduct(currentMembership)
        return {
            type: currentMembership,
            image: membershipProduct.lobbyImage,
            pending: this.membershipPending,
            title: membershipProduct.membershipTitle,
        }
    }

    getItems(): BaseStoreItem[] {
        return [
            ...this.presalePacks,
            ...this.expansions,
            ...this.maps,
            ...this.icons,
            ...this.colors,
        ]
    }

    hideAds(): boolean {
        if(this.hasMembership()) return true
        if(this.presalePacks.length > 0) return true

        return false
    }
}
